/**
 * Template: Solicitação de Acesso ao Sistema
 * 
 * Este template define um formulário para solicitar acesso a sistemas internos,
 * com campos condicionais que aparecem baseados na seleção do usuário.
 */

import type { TemplateSchema, TemplateSchemaOptions } from '../types'

// Mapeamento de Sistemas -> Módulos -> Perfis
const SISTEMAS_CONFIG = {
  EVO: {
    modulos: ['EVO'],
    perfis: {
      EVO: [
        'Acesso Total',
        'Administrador EVO',
        'Alta Gestão',
        'Financeiro',
        'Instrutores',
        'Instrutores (Coordenação)',
        'Recepção e Vendas',
      ],
    },
  },
  OAZEZ: {
    modulos: ['OAZEZ'],
    perfis: {
      OAZEZ: ['Agenda e GPS', 'Emissão de Guias'],
    },
  },
  PROTHEUS: {
    modulos: ['PROTHEUS'],
    perfis: {
      PROTHEUS: ['PROTHEUS_FIEAC', 'PROTHEUS_SESI', 'PROTHEUS_SENAI', 'PROTHEUS_IEL'],
    },
  },
  SGE: {
    modulos: ['SGE'],
    perfis: {
      SGE: [
        'Coordenador Pedagógico do SESI',
        'Secretaria Escolar do SESI',
        'Financeiro do SESI',
        'Coordenador Pedagógico do SENAI',
        'Secretaria Escolar do SENAI',
        'Financeiro do SENAI',
      ],
    },
  },
  TESTE: {
    modulos: ['TESTE'],
    perfis: {
      TESTE: ['TESTE'],
    },
  },
  PASTA: {
    modulos: ['PASTA DE UNIDADE'],
    perfis: {
      'PASTA DE UNIDADE': ['EDIÇÃO', 'LEITURA'],
    },
  },
  'E-MAIL': {
    modulos: ['GRUPO', 'CAIXA DE CORREIO'],
    perfis: {
      GRUPO: ['PARTICIPANTE'],
      'CAIXA DE CORREIO': ['PARTICIPANTE'],
    },
  },
}

/**
 * Configuração estática do fluxo simplificado para o RQ35.
 * Exportada separadamente para uso síncrono em logic-progressive.tsx.
 */
export const SOLICITACAO_ACESSO_SISTEMA_FLUXO_SIMPLIFICADO: NonNullable<TemplateSchema['fluxoSimplificado']> = {
  ativo: true,
  demandanteAutomatico: true,
  tituloAutomatico: true,
  prioridadePadrao: 'Media',
  diasEntregaPadrao: 3,
  ocultarInfoProcessos: true,
  ocultarTitulo: true,
  ocultarPrioridade: true,
  ocultarDataEntrega: true,
  ocultarAnexos: true,
  exibirHeaderTemplate: true,
}

/**
 * Gera o schema do template de Solicitação de Acesso ao Sistema
 * 
 * @param options - Opções para geração dinâmica (ex: lista de unidades)
 * @returns TemplateSchema configurado
 */
export function getSolicitacaoAcessoSistemaSchema(
  _options?: TemplateSchemaOptions,
): TemplateSchema {

  return {
    template_id: 'solicitacao_acesso_sistema',
    template_nome: 'Solicitação de Acesso ao Sistema',
    versao: '2.0.0',
    // Configuração de fluxo simplificado
    fluxoSimplificado: SOLICITACAO_ACESSO_SISTEMA_FLUXO_SIMPLIFICADO,
    schema: {
      type: 'object',
      title: 'Solicitação de Acesso ao Sistema',
      description: 'Preencha os dados para solicitar acesso aos sistemas da organização',
      required: ['nome_usuario', 'sistema', 'modulo', 'perfil'],
      properties: {
        nome_usuario: {
          type: 'string',
          title: 'Nome',
          description: 'Nome completo do usuário que receberá o acesso',
        },
        sistema: {
          type: 'string',
          title: 'Sistema',
          enum: ['', 'EVO', 'OAZEZ', 'PROTHEUS', 'SGE', 'TESTE', 'PASTA', 'E-MAIL'],
          default: '',
        },
        modulo: {
          type: 'string',
          title: 'Módulo',
          enum: [''],
          default: '',
        },
        perfil: {
          type: 'string',
          title: 'Perfil',
          enum: [''],
          default: '',
        },
        observacoes: {
          type: 'string',
          title: 'Observações',
          description: 'Informações adicionais sobre a solicitação',
        },
      },
      // Dependências condicionais
      dependencies: {
        sistema: {
          oneOf: [
            {
              properties: {
                sistema: { enum: [''] },
              },
            },
            ...Object.entries(SISTEMAS_CONFIG).map(([sistema, config]) => ({
              properties: {
                sistema: { enum: [sistema] },
                modulo: {
                  type: 'string' as const,
                  title: 'Módulo',
                  enum: ['', ...config.modulos],
                },
              },
            })),
          ],
        },
        modulo: {
          oneOf: [
            {
              properties: {
                modulo: { enum: [''] },
              },
            },
            ...Object.entries(SISTEMAS_CONFIG).flatMap(([_sistema, config]) =>
              Object.entries(config.perfis).map(([modulo, perfis]) => ({
                properties: {
                  modulo: { enum: [modulo] },
                  perfil: {
                    type: 'string' as const,
                    title: 'Perfil',
                    enum: ['', ...perfis],
                  },
                },
              }))
            ),
          ],
        },
      },
    } as const,

    // UI Schema para controlar aparência e comportamento
    uiSchema: {
      'ui:order': [
        'nome_usuario',
        'sistema',
        'modulo',
        'perfil',
        'observacoes',
      ],

      nome_usuario: {
        'ui:widget': 'user-selector',
        'ui:placeholder': 'Selecione o usuário...',
      },

      sistema: {
        'ui:placeholder': 'Selecione o sistema...',
        'ui:emptyValue': '',
      },

      modulo: {
        'ui:placeholder': 'Selecione o módulo...',
        'ui:emptyValue': '',
      },

      perfil: {
        'ui:placeholder': 'Selecione o perfil...',
        'ui:emptyValue': '',
      },

      observacoes: {
        'ui:widget': 'textarea',
        'ui:placeholder': 'Digite informações adicionais...',
        'ui:options': {
          rows: 4,
        },
      },
    },
  }
}

/**
 * Instância padrão do schema (sem unidades dinâmicas)
 */
export const solicitacaoAcessoSistemaSchema = getSolicitacaoAcessoSistemaSchema()
